import { orderBy } from 'lodash';

import { percentage, t } from '../localization';
import { groupBy } from './arrays';
import { average } from './math';
import { formatMoney } from './money';
import { getAssetClassGroupByAssetClass } from './portfolio';

// Generate month range string
export const monthRange = (i, monthsPerRound) => {
  if (monthsPerRound > 1) {
    return `${monthsPerRound * i + 1} - ${monthsPerRound * i + monthsPerRound}`;
  } else {
    return monthsPerRound * i + 1;
  }
};

export const getExpenseRatioAndAmountFormatted = ({
  costs,
  expenseRatio,
  formuePortfolio,
  currency = 'KR',
} = {}) => {
  let resolvedExpenseRatio;
  let resolvedExpenseAmount;

  // Prefer Salesforce Investment Strategy Cost data over exasol data.
  if (costs?.length) {
    const productCost = costs.find((item) => item.costType === 'productCost');
    const costByYear = Array.isArray(productCost?.costByYear)
      ? productCost.costByYear
      : [];

    const costData =
      costByYear.find((item) => item.year === 'year4plus') ??
      costByYear[costByYear.length - 1] ??
      {};

    resolvedExpenseRatio = costData.percentage;
    resolvedExpenseAmount = costData.amount;
  } else {
    resolvedExpenseRatio = expenseRatio;
    resolvedExpenseAmount = formuePortfolio * expenseRatio;
  }

  return {
    expenseRatioFormatted: percentage(resolvedExpenseRatio * 100, 2),
    expenseAmountFormatted: formatMoney(resolvedExpenseAmount, currency),
  };
};

export const formatInvestmentStrategyImplementationPlanData = (strategy) => {
  const result = strategy?.assetClasses?.reduce((acc, assetClassObj) => {
    const assetGroup = getAssetClassGroupByAssetClass(assetClassObj.assetClass);

    const existingGroup = acc.find((item) => item.assetGroup === assetGroup);

    if (existingGroup) {
      existingGroup.sharePerRound = existingGroup.sharePerRound.map(
        (value, index) => value + assetClassObj.sharePerRound[index]
      );
      existingGroup.target += assetClassObj.target;
    } else {
      acc.push({
        assetGroup,
        sharePerRound: [...assetClassObj.sharePerRound],
        target: assetClassObj.target,
      });
    }

    return acc;
  }, []);

  return orderBy(result, 'assetGroup', 'asc');
};

export const aggregateYearlyData = (data) => {
  const yearlyData = data?.reduce((acc, current) => {
    const currentDate = new Date(current.changedAt);
    const currentYear = currentDate.getFullYear();

    const existingYear = acc.find((item) => item.year === currentYear);

    if (existingYear) {
      existingYear.change += current.change;
    } else {
      acc.push({
        year: currentYear,
        change: current.change,
      });
    }

    return acc;
  }, []);

  return yearlyData;
};

export const getHistoricalPerformancePositiveYears = (data) => {
  const yearlyData = aggregateYearlyData(data);
  const positiveYears = yearlyData.filter((year) => year.change > 0);

  return positiveYears;
};

export const getHistoricalDrawdownNegativeYears = (data) => {
  const yearlyData = aggregateYearlyData(data);
  const positiveYears = yearlyData.filter((year) => year.change < 0);

  return positiveYears;
};

export const getYearlyReturn = (indexChanges) => {
  const groupedByYear = groupBy(indexChanges, (item) =>
    new Date(item.changedAt).getFullYear()
  );
  const yearlyReturns = [];
  Object.keys(groupedByYear).forEach((year) => {
    const changes = groupedByYear[year].map((values) => values.change);
    const annualAverage = changes.reduce((sum, a) => sum + a, 0);
    yearlyReturns.push({ year, change: annualAverage });
  });

  return yearlyReturns;
};

const signoffAcknowledgement = (id) => ({
  id,
  title: t(`app:wealthPlan:investmentStrategy:${id}:title`),
  description: t(`app:wealthPlan:investmentStrategy:${id}:description`),
  acceptanceText: t(`app:wealthPlan:investmentStrategy:${id}:acceptanceText`),
});

// The new check-offs the client must tick before signing, in page order, from
// the strategy's `consentsRequired`. `productLockin` is not the `lockIn` attribute.
export const getInvestmentStrategySignoffAcknowledgements = ({
  productLockin,
  productCommitment,
  investmentKnowledge,
}) =>
  [
    (productLockin || productCommitment) && {
      ...signoffAcknowledgement('alternativeInvestments'),
      buttonText: t(
        `app:wealthPlan:investmentStrategy:alternativeInvestments:buttonText`
      ),
    },
    investmentKnowledge && signoffAcknowledgement('knowledgeAndExperience'),
  ].filter(Boolean);

// Whether the client has given every consent the strategy asks for, so signing
// can go ahead. A check-off counts only once it has reported a tick.
export const investmentStrategyConsentsGiven = ({
  consentsRequired,
  termsAccepted,
  costsAccepted,
  acknowledgements,
}) =>
  !(consentsRequired.portfolioManagement && !termsAccepted) &&
  !(consentsRequired.costEstimate && !costsAccepted) &&
  getInvestmentStrategySignoffAcknowledgements(consentsRequired).every(
    ({ id }) => acknowledgements[id] === true
  );

const signoffPremise = (id) => ({
  id,
  title: t(`app:wealthPlan:investmentStrategy:summary:${id}:title`),
  text: t(`app:wealthPlan:investmentStrategy:summary:${id}:text`),
});

// What the client accepts by signing, listed in the strategy summary.
export const getInvestmentStrategySignoffPremises = (
  investmentStrategy,
  { productLockin, productCommitment }
) => {
  const managementType = investmentStrategy?.managementType;

  return [
    managementType === 'portfolioManagement' &&
      signoffPremise('portfolioManagement'),
    // The advisory copy hasn't been written yet.
    managementType === 'advisory' && signoffPremise('advisory'),
    productLockin && signoffPremise('lockIn'),
    productCommitment && signoffPremise('commitment'),
    signoffPremise('knowledgeAndExperience'),
    signoffPremise('costs'),
  ].filter(Boolean);
};
